import { Component, Vue, Prop as prop, Watch as watch } from 'nuxt-property-decorator';
import { mapGetters, mapState } from 'vuex';
import debounce from 'lodash/debounce';
import { AxiosError } from 'axios';
import { clone, cloneDeep, Dictionary, isEqual } from 'lodash';
import { CatalogDataResponse, CatalogFilterField, Descriptions, Offer, OfferList } from '~/types/catalog';

interface ICatalogChild {
    label: string;
    link: string;
    icon: string | null;
    canonical: string | null;
}

@Component({
    components: {
        offer: require('~/components/catalog/offer/').default,
        filterElement: require('~/components/catalog/filter/').default,
        SadFace: require('~/components/common/sad-face-block/').default,
        InfiniteContainer: require('~/components/catalog/infinite-container/').default,
        pagination: require('~/components/catalog/pagination').default,
        customPicture: require('~/components/common/simple-picture/').default,
        // whyBuy: require('~/components/common/why-buy/').default,
        recentViewList: require('~/components/catalog/recent-view-list').default,
    },
    watchQuery: ['tag'],
    computed: {...mapGetters(['region']), ...mapState(['user'])},
})
export default class DiscontCatalogPageComponent extends Vue {

    //#region receivedProps

    protected items: Offer[] = null;
    protected metaData: Descriptions = null;
    protected filter: CatalogFilterField[] = [];
    protected pageCount = 1;
    protected id: Number = 0;
    protected code: string = null;
    protected hasItems: boolean = false;
    protected promo: any = false;
    protected banners = [];

    //#endregion

    $refs: {
        filter?: Vue,
        container?: Vue,
    }

    public user!: User;

    private orderBy: string = 'default';
    private page = 1;
    private initialPage = 1;
    private loading = false;
    private simplifiedFilter = [];
    private debouncedSearch = debounce(this.reset, 300);
    private topList: OfferList = null;

    private oldFilter: any[] = [];
    private oldOrderBy = 'default';
    public childs: ICatalogChild[];
    currentTag: string = null;
    showBanner = false;

    public async loadMore(page: number) {

        if (this.loading || page === this.page) {
            return;
        }

        const desiredPage = page;

        if (desiredPage > this.pageCount || desiredPage < 1) {
            return;
        }

        this.updateStoredFilterValues(page);

        this.page = page;

        this.loading = true;

        let data: CatalogDataResponse = null;

        const shouldGetPageCount = !isEqual(this.oldFilter, this.simplifiedFilter) || this.oldOrderBy !== this.orderBy;

        try {

            this.oldFilter = cloneDeep(this.simplifiedFilter);
            this.oldOrderBy = this.orderBy;

            data = await this.$store.dispatch('discont-catalog/Page', {
                code: this.code,
                page: desiredPage,
                filter: this.simplifiedFilter,
                order: this.orderBy,
                tag: this.currentTag,
                getPages: shouldGetPageCount,
                withMeta: true,
                promo: true,
                banners: true,
            });

            this.items = data.items;

            if (data.meta) {
                this.metaData = data.meta;
            }

            if (data.pages) {
                this.pageCount = data.pages;
            }

            this.$nextTick(() => {
                let el = this.$refs.filter?.$el ?? this.$refs.container?.$el;
                if (el) {
                    if (el.getBoundingClientRect().top < 0) {
                        el.scrollIntoView({behavior: 'smooth'});
                    }
                }
            });

            if (this.orderBy !== this.oldOrderBy || !isEqual(this.oldFilter, this.simplifiedFilter)) {
                this.loadMore(this.page);
            }
        } catch (error) {
            throw {statusCode: 500, message: 'dead'};
        } finally {
            this.loading = false;
        }
    }

    private async asyncData({store, error, params, route, redirect}) {

        // console.log(params)

        let data: CatalogDataResponse,
            code = params.code,
            page = route.query.page ? Number(route.query.page) : 1,
            orderBy = route.query.orderBy || 'default',
            initialPage = page,
            currentTag = route.query.tag ?? null,
            filter = [];

        if ((route.query.page && (isNaN(route.query.page) || route.query.page < 2))) {
            if (page > 1) {
                redirect(301, `/catalog/discont/${params.code}/?page=${page}`);
            } else {
                redirect(301, `/catalog/discont/${params.code}/`);
            }

            return;
        }

        try {
            data = await store.dispatch('discont-catalog/Full', {
                code,
                page,
                order: orderBy,
                params: route.query,
                tag: currentTag,
                promo: true,
                banners: true,
            });

            if (data.pages && data.pages < page) {
                redirect(301, `/catalog/${params.path}/`);
            }

            if (data.filter) {
                filter = createFilter(data.filter);
                fillFilterFromQuery(route.query, filter);
            }

            if (data.breadcrumbs) {
                store.commit('setBreadcrumbs', data.breadcrumbs);
            }

            let topList: OfferList = null;

            if (data.top_offers) {
                topList = {
                    id: 'top-offers',
                    offers: data.top_offers,
                    name: 'Хиты продаж'
                };
            }
    
            return {
                banners: data.banners,
                items: data.items,
                promo: data.promo,
                metaData: data.meta,
                pageCount: data.pages,
                hasItems: data.has_items,
                childs: data.childs,
                currentTag,
                topList,
                code,
                page,
                initialPage,
                filter,
                orderBy,
                oldOrderBy: orderBy
            };

        } catch (err) {

            if (typeof (err as any).response !== 'undefined') {

                switch ((err as AxiosError).response.status) {
                    case 404:
                        error({ statusCode: 404 });
                        break;
                    case 403: 
                        error({ statusCode: 403, description: 'Каталог не доступен в вашем регионе' });
                        break;
                    default:
                        error({ statusCode: 500 });
                }
            } else {
                error({ statusCode: 500 });
            }
        }
    }

    @watch('filter', {deep: true})
    private onFilterChange() {
        if (!this.loading) {
            this.debouncedSearch();
        }
    }

    updateSort(value) {
        this.orderBy = value;
        this.reset();
    }

    toggleTag(value) {
        if (this.currentTag === value) {
            this.currentTag = null;
        } else {
            this.currentTag = value;
        }

        this.reset();
    }

    private reset() {
        this.page = 0;
        this.initialPage = 1;
        this.pageCount = 1;
        this.buildSimplifiedFilter();
        this.loadMore(1);
    }

    public buildSimplifiedFilter() {
        this.simplifiedFilter.length = 0;

        if (this.filter) {
            const compoundFilters = new Map();

            for (const field of this.filter) {
                if (field.type === 'range') {
                    if (!isEqual(field.bounds, field.values)) {
                        this.simplifiedFilter.push({
                            code: field.code,
                            values: field.values
                        });
                    }
                } else if (field.type === 'bool' && field.values) {
                    this.simplifiedFilter.push({
                        code: field.code,
                        values: !!field.values
                    });
                } else if (field.type === 'compound' && field.values) {
                    const value = compoundFilters.get(field.code) ?? [];
                    value.push(field.value);
                    compoundFilters.set(field.code, value);
                } else if (field.values.length && field.values.length !== field.set.length) {
                    this.simplifiedFilter.push({
                        code: field.code,
                        values: field.values.map((el) => el.value),
                    });
                }
            }

            compoundFilters.forEach((value, key) => {
                this.simplifiedFilter.push({
                    code: key,
                    values: value
                });
            })
        }
    }

    @watch('region')
    private async onRegionChange() {
        let data: CatalogDataResponse;

        this.loading = true;

        try {
            data = await this.$store.dispatch('discont-catalog/Full', {
                code: this.code,
                page: 1,
                promo: true,
                banners: true,
            });

            if (data.filter) {
                this.filter = createFilter(data.filter);
                fillFilterFromQuery(this.$route.query, this.filter);
            }

            this.promo = data.promo;
            this.banners = data.banners;
            this.metaData = data.meta;
            this.page = 1;
            this.initialPage = 1;
            this.pageCount = data.pages;
            this.items = data.items;
            this.hasItems = data.has_items;

        } catch (er) {
            throw {statusCode: 404};
        } finally {
            this.$nextTick(() => {
                this.loading = false;
            });
        }
    }

    public head() {

        // let canonLink = this.metaData?.canonical ?? null;

        // if (this.currentTag) {
        //     const foundTag = this.childs.find((el) => el.label === this.currentTag);

        //     if (foundTag?.canonical) {
        //         canonLink = foundTag.canonical;
        //     }
        // }

        // if (canonLink?.startsWith('/')) {
        //     canonLink = `https://www.triya.ru${canonLink}`;
        // }

        return {
            title: 'Распродажа - интернет магазин ТриЯ',
        }
        // return {
        //     title: this.metaData.title,
        //     meta: [
        //         { vmid: 'description', name: 'description', content: this.metaData.description },
        //     ],
        //     link: [
        //         { rel: 'canonical', href: canonLink },
        //     ],
        // }
    }

    public updateStoredFilterValues(page: number) {

        const routeParams = {};

        if (this.orderBy !== 'default') {
            routeParams['orderBy'] = this.orderBy;
        }

        if (page > 1) {
            routeParams['page'] = page;
        }

        const activeFilters = this.filter.filter((item) => {
            if (item.type === 'material' && !item.values?.length) {
                return false;
            } else if (item.type === 'range' && isEqual(item.values, item.bounds)) {
                return false;
            } else if (item.type === 'bool' && !item.values) {
                return false;
            } else if (item.type === 'compound' && !item.values) {
                return false;
            }

            return true;
        });

        if (activeFilters.length) {
            activeFilters.forEach((item) => {
                if (item.type === 'range') {
                    routeParams[item.code] = `${item.values[0]},${item.values[1]}`;
                } else if (item.type === 'bool') {
                    routeParams[item.code] = null;
                } else if (item.type === 'compound') {
                    if (typeof routeParams[item.code] !== 'undefined') {
                        routeParams[item.code] += ',' + item.value;
                    } else {
                        routeParams[item.code] = item.value;
                    }
                } else {
                    routeParams[item.code] = item.values.map((el) => el.value).join(',');
                }
            });
        }

        if (this.currentTag) {
            routeParams['tag'] = this.currentTag;
        }

        this.$router.replace({
            query: routeParams
        })
    }
}

function createFilter(fields: CatalogFilterField[]) {

    const filter = [];

    for (const field of fields) {
        if (field.type === 'range' && field.bounds && field.bounds[0] !== field.bounds[1]) {
            filter.push(Object.assign(field, {values: clone(field.bounds)}));
        } else if (field.set?.length) {
            filter.push(Object.assign(field, {values: []}));
        } else if (field.type === 'bool') {
            filter.push(Object.assign(field, {values: false}));
        } else if (field.type === 'compound') {
            filter.push(Object.assign(field, {values: false}));
        }
    }

    return filter;
}

function fillFilterFromQuery(query: Dictionary<string|string[]>, filter: CatalogFilterField[]) {

    filter.forEach((field) => {
        if (field.type === 'bool' && typeof query[field.code] !== 'undefined') {
            field.values = true;
            return;
        }

        if (typeof query[field.code] !== 'string') {
            return;
        }

        if (field.type === 'range') {
            /** @ts-ignore */
            let parts = query[field.code].split(',').map((el) => parseInt(el)).filter((el) => !isNaN(el));

            if (parts.length !== 2 || parts[1] < parts[0]) {
                return;
            }

            field.values = parts;

            if (field.bounds[0] > field.values[0]) {
                field.values[0] = field.bounds[0];
            }
            if (field.bounds[1] < field.values[1]) {
                field.values[1] = field.bounds[1];
            }
        } else if (field.type === 'material') {

            const existsValues = new Map(field.set.map((el) => [el.value, el]));

            /** @ts-ignore */
            field.values = query[field.code].split(',').filter((el) => existsValues.has(el)).map((el) => existsValues.get(el));
        } else if (field.type === 'compound') {
            /** @ts-ignore */
            field.values = query[field.code].split(',').includes(field.value);
        }
    })
}
